import React from 'react';
import PropTypes from 'prop-types';
import { Box, DialogContent } from '@mui/material';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import { operationalScrollbars } from '../../../../design-system/tokens';
import ArticleModalAuxiliaryDialogs from './ArticleModalAuxiliaryDialogs';
import ArticleModalFooterBar from './ArticleModalFooterBar';
import ArticleModalKpiaFace from './ArticleModalKpiaFace';
import ArticleModalLoadedContent from './ArticleModalLoadedContent';
import ArticleModalLoadingContent from './ArticleModalLoadingContent';
import ArticleModalNavigationArrows from './ArticleModalNavigationArrows';

const ArticleModalView = ({
  article,
  availableTags,
  auxiliary,
  content,
  dialog,
  footer,
  kpia,
  navigation,
  readOnly = false
}) => (
  <>
    <ArticleModalNavigationArrows {...navigation} />

    <PlatformDialog
      open={dialog.open}
      onClose={dialog.onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      aria-labelledby="article-dialog-title"
      className="article-reading-modal"
      sx={{
        '& .MuiDialog-container': {
          perspective: { md: '1800px' }
        }
      }}
      BackdropProps={{
        sx: { backgroundColor: 'rgba(0,0,0,0.9)' }
      }}
      PaperProps={{
        onTouchStart: dialog.onTouchStart,
        onTouchEnd: dialog.onTouchEnd,
        sx: {
          position: 'relative',
          minHeight: { xs: '100dvh', sm: '90vh' },
          maxHeight: { xs: '100dvh', sm: '95vh' },
          height: { xs: '100dvh', sm: 'auto' },
          margin: { xs: 0, sm: 2 },
          width: { xs: '100%', sm: 'calc(100% - 32px)' },
          maxWidth: { xs: '100%', sm: 'lg' },
          borderRadius: { xs: 0, sm: 3 },
          overflow: 'hidden',
          perspective: { md: '1800px' }
        }
      }}
    >
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          perspective: { md: '1800px' }
        }}
      >
        <ArticleModalKpiaFace
          article={article}
          initialSection={kpia.initialSection}
          loading={content.loadingFullContent}
          open={kpia.open}
          onClose={kpia.onClose}
          onExit={dialog.onClose}
          onRecalculate={kpia.onRecalculate}
        />

        <Box
          sx={{
            position: { xs: 'relative', md: 'absolute' },
            inset: { md: 0 },
            width: '100%',
            height: '100%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            pointerEvents: kpia.open ? 'none' : 'auto',
            zIndex: kpia.open ? 1 : 2,
            opacity: { xs: kpia.open ? 0 : 1, md: kpia.open ? 0 : 1 },
            transform: { md: kpia.open ? 'rotateY(-180deg)' : 'rotateY(0deg)' },
            transformOrigin: 'center center',
            transition: {
              xs: 'opacity 180ms cubic-bezier(0.22, 1, 0.36, 1)',
              md: 'transform 560ms cubic-bezier(0.22, 1, 0.36, 1), opacity 1ms linear 230ms'
            },
            backfaceVisibility: { md: 'hidden' },
            WebkitBackfaceVisibility: { md: 'hidden' }
          }}
        >
          <DialogContent
            sx={{
              ...operationalScrollbars.default,
              flex: 1,
              minHeight: 0,
              height: '100%',
              overflowX: 'visible',
              px: { xs: 1.5, sm: 3 }
            }}
          >
            {content.loadingFullContent ? (
              <ArticleModalLoadingContent
                article={article}
                onClose={dialog.onClose}
              />
            ) : (
              <ArticleModalLoadedContent
                {...content.loadedContentProps}
                article={article}
                kpiaAnalyzing={kpia.kpiaAnalyzing}
                qualitativeAnalysisApplied={kpia.qualitativeAnalysisApplied}
                readOnly={readOnly}
              />
            )}
          </DialogContent>

          <ArticleModalFooterBar
            {...footer}
            article={article}
            kpiaAnalyzing={kpia.kpiaAnalyzing}
            qualitativeAnalysisApplied={kpia.qualitativeAnalysisApplied}
            readOnly={readOnly}
          />
        </Box>
      </Box>
    </PlatformDialog>

    <ArticleModalAuxiliaryDialogs
      {...auxiliary}
      article={article}
      availableTags={availableTags}
      readOnly={readOnly}
    />
  </>
);

ArticleModalView.propTypes = {
  article: PropTypes.object.isRequired,
  availableTags: PropTypes.array.isRequired,
  auxiliary: PropTypes.object.isRequired,
  content: PropTypes.shape({
    loadedContentProps: PropTypes.object.isRequired,
    loadingFullContent: PropTypes.bool.isRequired
  }).isRequired,
  dialog: PropTypes.shape({
    onClose: PropTypes.func.isRequired,
    onTouchEnd: PropTypes.func.isRequired,
    onTouchStart: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired
  }).isRequired,
  footer: PropTypes.object.isRequired,
  kpia: PropTypes.shape({
    initialSection: PropTypes.oneOf(['smartscore', 'performance']).isRequired,
    kpiaAnalyzing: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onRecalculate: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    qualitativeAnalysisApplied: PropTypes.bool.isRequired
  }).isRequired,
  navigation: PropTypes.object.isRequired,
  readOnly: PropTypes.bool
};

ArticleModalView.defaultProps = {
  readOnly: false
};

export default ArticleModalView;
